import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
//Stores & Actions
import UserAction from 'actions/UserAction';
import UserStore from 'stores/UserStore';
import AccountAction from 'actions/AccountAction';
import AccountStore from 'stores/AccountStore';
//Styles
import { useStyles } from 'components/sheets/UserActionsSheet/UserActionsFiltersSelect.style';
//Mui components
import {
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/fr';
//Locales
import translate from 'locales/i18n';

const actions = [
    'LOGOUT',
    'LOGIN',
    'SPE_EXPORT',
    'ADBOX_ANALYSIS',
    'PERF_ANALYSIS',
    'PRICING_ENGINE_ANALYSIS',
    'SLIDER_ANALYSIS',
    'SPE_ANALYSIS',
    'TRENDS_ANALYSIS'
];

const granularities = [
    'DAY',
    'MONTH'
];

const UserActionsFiltersSelect = ({
    fromStr,
    isGraphPage,
    isLoading,
    selectedAccounts,
    selectedActions,
    selectedGranularity,
    selectedUsers,
    setFromStr,
    setSelectedAccounts,
    setSelectedActions,
    setSelectedGranularity,
    setSelectedUsers,
    setToStr,
    toStr
}) => {
    const [accounts, setAccounts] = useState(AccountStore.getAccounts());
    const [users, setUsers] = useState([]);

    const [openFstDatePicker, setOpenFstDatePicker] = useState(false);
    const [openSndDatePicker, setOpenSndDatePicker] = useState(false);

    const adapter = new AdapterDayjs();

    const classes = useStyles();

    useEffect(() => {
        UserAction.getUsers();
        AccountAction.getAccounts();

        UserStore.addChangeListener(onUserStoreChange);
        AccountStore.addChangeListener(onAccountStoreChange);

        return () => {
            UserStore.removeChangeListener(onUserStoreChange);
            AccountStore.removeChangeListener(onAccountStoreChange);
        };
    }, []);

    const onUserStoreChange = () => setUsers(UserStore.getUsers().filter(user => user.isSuperUser === false));
    const onAccountStoreChange = () => setAccounts(AccountStore.getAccounts());

    const handleChange = (value, selector) => {
        if (selector === 'USER') setSelectedUsers(typeof value === 'string' ? value.split(',') : value);
        if (selector === 'ACCOUNT') setSelectedAccounts(typeof value === 'string' ? value.split(',') : value);
        if (selector === 'ACTION') setSelectedActions(typeof value === 'string' ? value.split(',') : value);
        if (selector === 'GRANULARITY') setSelectedGranularity(value);
    };

    return <div className={classes.selectorsContainer}>
        <div className={classes.selector} style={{ marginLeft: '10px' }}>
            <FormControl style={{ width: '100%' }}>
                <InputLabel id='userSelectLabel' size='small'>{translate.i18n('USERS')}</InputLabel>
                <Select
                    labelId='userSelectLabel'
                    id='userSelect'
                    multiple
                    size='small'
                    value={selectedUsers}
                    onChange={(event) => {
                        handleChange(event.target.value, 'USER');
                    }}
                    input={<OutlinedInput label={translate.i18n('USERS')} />}
                    renderValue={(selected) => selected.join(', ')}
                >
                    {users.map((user) => <MenuItem key={user.id} value={user.login} >
                        <Checkbox checked={selectedUsers.indexOf(user.login) > -1} />
                        <ListItemText primary={user.login} />
                    </MenuItem>
                    )}
                </Select>
            </FormControl>
        </div>
        {!isGraphPage && <div className={classes.selector} style={{ marginLeft: '10px' }}>
            <FormControl style={{ width: '100%' }}>
                <InputLabel id='userSelectLabel' size='small'>{translate.i18n('ACCOUNTS')}</InputLabel>
                <Select
                    labelId='accountSelectLabel'
                    id='accountSelect'
                    multiple
                    size='small'
                    value={selectedAccounts}
                    onChange={(event) => handleChange(event.target.value, 'ACCOUNT')}
                    input={<OutlinedInput label={translate.i18n('ACCOUNTS')} />}
                    renderValue={(selected) => selected.join(', ')}
                >
                    {accounts.map((account) => <MenuItem key={account.id} value={account.name}>
                        <Checkbox checked={selectedAccounts.indexOf(account.name) > -1} />
                        <ListItemText primary={account.name} />
                    </MenuItem>
                    )}
                </Select>
            </FormControl>
        </div>
        }
        <div className={classes.selector} style={{ marginLeft: '10px' }}>
            <FormControl style={{ width: '100%' }}>
                <InputLabel id='userSelectLabel' size='small'>{translate.i18n('ACTIONS')}</InputLabel>
                <Select
                    labelId='actionSelectLabel'
                    id='actionSelect'
                    multiple
                    size='small'
                    value={selectedActions}
                    onChange={(event) => handleChange(event.target.value, 'ACTION')}
                    input={<OutlinedInput label={translate.i18n('ACTIONS')} />}
                    renderValue={(selected) => selected.join(', ')}
                >
                    {actions.map((action) => <MenuItem key={action} value={action}>
                        <Checkbox checked={selectedActions.indexOf(action) > -1} />
                        <ListItemText primary={action} />
                    </MenuItem>
                    )}
                </Select>
            </FormControl>
        </div>
        <div style={{ marginLeft: '30px', display: 'flex' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'fr'}>
                <DatePicker
                    value={fromStr}
                    maxDate={adapter.date(toStr)}
                    onChange={(newValue) => {
                        setFromStr(newValue.$d);
                    }}
                    open={openFstDatePicker}
                    disabled={isLoading}
                    label={translate.i18n('FROM')}
                    onClose={() => {
                        setOpenFstDatePicker(false);
                        setOpenSndDatePicker(true);
                    }}
                    renderInput={(params) => <TextField {...params} onClick={() => !isLoading && setOpenFstDatePicker(true)} size='small' />}
                />
            </LocalizationProvider>
            <div style={{ marginLeft: '15px' }} />
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'fr'}>
                <DatePicker
                    value={toStr}
                    minDate={adapter.date(fromStr)}
                    maxDate={adapter.date(new Date())}
                    label={translate.i18n('TO')}
                    disabled={isLoading}
                    open={openSndDatePicker}
                    onChange={(newValue) => {
                        setToStr(newValue.$d);
                    }}
                    onClose={() => {
                        setOpenSndDatePicker(false);
                    }}
                    renderInput={(params) => <TextField {...params} onClick={() => !isLoading && setOpenSndDatePicker(true)} size='small' />}
                />
            </LocalizationProvider>
        </div>
        {isGraphPage && <div className={classes.selector} style={{ marginLeft: '10px' }}>
            <FormControl style={{ width: '100%' }}>
                <InputLabel id='granularitySelectLabel' size='small'>{translate.i18n('GRANULARITY')}</InputLabel>
                <Select
                    labelId='granularitySelectLabel'
                    id='granularitySelect'
                    size='small'
                    value={selectedGranularity}
                    label='Granularity'
                    onChange={(event) => handleChange(event.target.value, 'GRANULARITY')}
                >
                    {granularities.map((gran) => <MenuItem key={gran} value={gran}> {translate.i18n(gran)} </MenuItem>)}
                </Select>
            </FormControl>
        </div>
        }
    </div>;

};

UserActionsFiltersSelect.propTypes = {
    fromStr: PropTypes.object,
    isGraphPage: PropTypes.bool,
    isLoading: PropTypes.bool,
    selectedAccounts: PropTypes.array,
    selectedActions: PropTypes.array,
    selectedGranularity: PropTypes.string,
    selectedUsers: PropTypes.array,
    setFromStr: PropTypes.func,
    setSelectedAccounts: PropTypes.func,
    setSelectedActions: PropTypes.func,
    setSelectedGranularity: PropTypes.func,
    setSelectedUsers: PropTypes.func,
    setToStr: PropTypes.func,
    toStr: PropTypes.object
};

export default UserActionsFiltersSelect;
